import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./LoginCode.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { headerConfig } from "../../../axiosconfig.js";
import { toast } from "react-toastify";

export const LoginCode = () => {
  const [loginCode, setLoginCode] = useState("");
  const [isCodeValid, setIsCodeValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const location = useLocation();
  const navigate = useNavigate();
  const email = location.state?.email || "";
  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    if (!email) {
      toast.error("Something went wrong, sending you to home page.");
      navigate("/");
    }
  }, [email, navigate]);

  const validateCode = (codeInput) => {
    const isValid = codeInput.length === 6 && /^[A-Z0-9]+$/.test(codeInput);
    setIsCodeValid(isValid);
  };

  const handleCodeChange = (e) => {
    const codeInput = e.target.value.toUpperCase();
    setLoginCode(codeInput);
    validateCode(codeInput);
    if (errorMessage) {
      setErrorMessage("");
    }
  };

  const handleVerifyOTP = async () => {
    setIsLoading(true);
    setErrorMessage("");

    try {
      const response = await axios.post(
        `${API_URL}/register`,
        {
          request: "complete",
          email: email,
          code: loginCode,
        },
        headerConfig
      );

      if (response.data.result) {
        navigate("/TermsConditions", { state: { email, loginCode } });
      } else {
        if (response.data.code === -2) {
          const msg =
            "Your code has expired. A new email with a fresh code has been sent. Please check your inbox.";
          setErrorMessage(msg);
          toast.error(msg);
        } else {
          const msg = "The verification code you provided is invalid. Please try again.";
          setErrorMessage(msg);
          toast.error(msg);
        }
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      const msg = "Error verifying code. Please try again.";
      setErrorMessage(msg);
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);
    setErrorMessage("");

    try {
      const response = await axios.post(
        `${API_URL}/register`,
        {
          request: "resend",
          email: email,
        },
        headerConfig
      );

      if (response.data.result) {
        toast.info("A new verification code has been sent to your email.");
      } else {
        toast.error("Failed to resend code. Please try again later.");
      }
    } catch (error) {
      console.error("Error resending code:", error);
      toast.error("Failed to resend code. Please try again later.");
    } finally {
      setIsResending(false);
    }
  };

  const handleContinueClick = () => {
    if (isCodeValid) {
      handleVerifyOTP();
    } else {
      toast.warn("Please enter a valid 6-digit verification code.");
    }
  };

  return (
    <div className="login-code-page-wrapper">
      {/* Top Navigation / Glassmorphic Back button */}
      <button
        type="button"
        className="login-code-back-btn"
        onClick={() => navigate(-1)}
        aria-label="Go Back"
      >
        <span className="login-code-back-arrow" aria-hidden="true">
          &larr;
        </span>
        <span className="login-code-back-label">Back</span>
      </button>

      {/* 1. TOP BRAND SECTION (Matching previous pages) */}
      <div className="login-code-brand-section">
        <div className="login-code-logo-row">
          <svg
            className="login-code-brand-logo-svg"
            width="58"
            height="51"
            viewBox="0 0 56 49"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-label="Mindsherpa Logo"
          >
            <path
              d="M28.1326 9.25302L31.7566 14.669L31.9724 15.004C34.061 18.3863 34.1512 22.6752 32.2068 26.1417L32.0056 26.4854L29.9285 29.8878V29.8888C29.8904 29.9508 29.848 30.0075 29.8025 30.0597L29.6375 30.2491L29.5496 30.0147L28.8826 28.253C28.667 27.6827 28.7255 27.0447 29.0437 26.5235L29.8601 25.1856L30.0154 24.92C31.5175 22.2423 31.4484 18.9278 29.8347 16.3145L29.6687 16.0558L27.989 13.5479L25.7957 16.7686C24.1351 19.2044 23.8543 22.2673 25.0437 24.964L27.6316 30.8263L27.6726 30.92L27.6707 30.921C28.9455 33.8744 29.0206 37.1281 27.8865 40.129L27.7722 40.422C26.5465 43.4366 24.2404 45.7945 21.2595 47.0899L20.9685 47.212C19.4209 47.8394 17.7837 48.1494 16.1521 48.1495C13.9162 48.1495 11.6914 47.5684 9.71948 46.4268L9.32788 46.1915L1.34253 41.1778L1.11011 41.0313L1.35913 40.9151L9.35229 37.1632L9.71069 37.0011C13.4366 35.39 17.7188 35.6344 21.2429 37.6808L25.2205 39.9893C25.2993 39.8228 25.3763 39.6551 25.446 39.4825V39.4815L25.6228 39.0138C26.3914 36.8186 26.3568 34.4686 25.5242 32.297L25.3337 31.8351L22.7468 25.9718C21.2056 22.4825 21.5698 18.516 23.7195 15.3624L27.8835 9.25107L28.0085 9.06845L28.1326 9.25302ZM19.9802 39.8419C17.1481 38.1972 13.7068 38.0003 10.7107 39.296L10.4226 39.4259L6.3269 41.3487L10.6658 44.0743L10.9304 44.2345C13.6818 45.8471 17.0562 46.0972 20.0222 44.8936L20.0496 44.8829C21.5598 44.2658 22.8592 43.312 23.8728 42.1017L19.9802 39.8419Z"
              fill="#094B38"
              stroke="#094B38"
              strokeWidth="0.3"
            />
            <path
              d="M35.0972 37.4904C38.6395 35.5765 42.8826 35.4352 46.5493 37.1193L46.5532 37.1164L46.6489 37.1613L54.6411 40.9142L54.8892 41.0314L54.6567 41.1769L46.6724 46.1906C46.1757 46.5024 45.6557 46.7815 45.1206 47.0226L44.5806 47.2512C43.1253 47.8277 41.536 48.1193 39.8569 48.1193H25.4292C25.2772 48.1193 25.1314 48.0924 24.9956 48.0422L24.729 47.9435L24.9604 47.7795C25.5061 47.391 26.022 46.9666 26.5063 46.509L26.7437 46.3058C27.3155 45.8614 28.0226 45.6174 28.7534 45.6174H39.8569C41.2201 45.6173 42.4959 45.3831 43.6519 44.926L44.0864 44.7424C44.517 44.5484 44.935 44.3246 45.3335 44.0744L49.6733 41.3488L45.5786 39.426C42.5174 37.9892 38.9444 38.1452 36.021 39.843L36.02 39.842L28.9897 43.9592L28.7876 43.7492C29.2706 42.9924 29.6885 42.1855 30.0356 41.3342L30.0366 41.3312L30.0396 41.3234L30.1655 41.048C30.4831 40.42 30.9763 39.894 31.5884 39.5353L34.7534 37.6828L35.0972 37.4904Z"
              fill="#094B38"
              stroke="#094B38"
              strokeWidth="0.3"
            />
            <path
              d="M22.0286 6.1965C22.0285 7.0587 21.7361 7.8926 21.2004 8.56564L21.0901 8.69748L19.5735 10.4348C15.1105 15.5517 14.2599 22.9239 17.2922 28.8869L17.4524 29.2268C17.8024 30.0276 17.9855 30.892 17.9856 31.7688V34.6057L17.7288 34.3488L17.718 34.3371L17.7092 34.3235L16.01 31.6848C13.7496 28.1753 12.7146 24.121 13.0178 19.9612L13.05 19.5725C13.4235 15.5624 15.0189 11.8415 17.6799 8.79221L21.7659 4.11057L22.0286 3.80881V6.1965Z"
              fill="#094B38"
              stroke="#094B38"
              strokeWidth="0.3"
            />
            <path
              d="M34.0864 4.1113L38.1714 8.79294L38.4253 9.09079C41.0166 12.1822 42.5387 15.9329 42.8325 19.9619L42.8569 20.3515C43.0636 24.2448 42.0968 28.0328 40.0503 31.3545L39.8423 31.6855L38.1431 34.3242L37.8667 34.7539V31.7695C37.8667 30.7679 38.1058 29.7811 38.5601 28.8877L38.8335 28.3252C41.5321 22.4743 40.6042 15.4121 36.2954 10.456L36.2778 10.4355L34.7612 8.69821C34.1564 8.00379 33.8228 7.11607 33.8228 6.19724V3.80954L34.0864 4.1113Z"
              fill="#094B38"
              stroke="#094B38"
              strokeWidth="0.3"
            />
            <path
              d="M28 1.00006C29.4093 1.00026 30.5428 2.13557 30.543 3.52545C30.543 4.91548 29.4094 6.05162 28 6.05182C26.5904 6.05182 25.4561 4.9156 25.4561 3.52545C25.4562 2.13545 26.5906 1.00006 28 1.00006Z"
              stroke="#094B38"
              strokeWidth="2"
            />
          </svg>
          <span className="login-code-brand-name">mindsherpa</span>
        </div>
        <p className="login-code-brand-tagline">
          Your space to de-stress, and support each other at work.
        </p>
      </div>

      {/* 2. MIDDLE CONTENT (Form centered vertically) */}
      <div className="login-code-main-card">
        {/* Heading & Subtitle */}
        <div className="login-code-heading-group">
          <h1 className="login-code-page-title">Start Your Mindsherpa Journey</h1>
          <p className="login-code-page-subtitle">
            We sent a temporary login code to (<strong>{email}</strong>) &bull;{" "}
            <button
              type="button"
              className="login-code-resend-btn"
              onClick={handleResendCode}
              disabled={isResending}
            >
              {isResending ? "Resending..." : "Resend Code?"}
            </button>
          </p>
        </div>

        {/* Form Container */}
        <div className="login-code-form">
          <div className="login-code-field-group">
            <label htmlFor="loginCode" className="login-code-label">
              Login Code <span className="login-code-required">*</span>
            </label>
            <div className="login-code-input-wrapper">
              <FontAwesomeIcon icon={faLock} className="login-code-field-icon" />
              <input
                type="text"
                id="loginCode"
                className="login-code-input"
                value={loginCode}
                onChange={handleCodeChange}
                placeholder="Enter your 6-digit pin"
                maxLength={6}
                autoComplete="one-time-code"
              />
            </div>
          </div>

          {/* Error Alert Box */}
          {errorMessage && (
            <div className="login-code-error-alert" role="alert">
              {errorMessage}
            </div>
          )}

          {/* Continue Button */}
          <button
            type="button"
            className="login-code-submit-btn"
            onClick={handleContinueClick}
            disabled={!isCodeValid || isLoading}
          >
            {isLoading ? "Verifying..." : "Continue"}
          </button>
        </div>
      </div>

      {/* 3. BOTTOM FOOTER LINK */}
      <div className="login-code-footer-container">
        <span className="login-code-footer-text">Have an account?&nbsp;</span>
        <a href="/login" className="login-code-footer-link">
          Login
        </a>
      </div>
    </div>
  );
};

export default LoginCode;
